import { BOOL_COLUMN_TYPE } from "../types/constants";
import {
  SAVE_BLOCKED_JOINS_INCOMPLETE,
  SAVE_BLOCKED_NAME_MISSING,
  SAVE_BLOCKED_NAME_NOT_UNIQUE,
  SAVE_BLOCKED_NO_COLUMNS,
} from "../types/constants";
import { getDefaultAggregation } from "./aggregationUtils";
import { extractVarColumns } from "./expressionEditorUtils";
import {
  buildDateFilterSpecs,
  buildNumberFilterSpecs,
  buildValueFilterSpec,
} from "./filterSpecUtils";
import { joinsFormSingleResult } from "./joinUtils";
import { SpecBuilder } from "./specBuilderSupport";
import { getColumnSourceSeparator } from "config";

// Drops every entry of a pre-aggregation filter map whose column is no longer
// an output column. The original map is returned when nothing was removed, so
// callers can use an identity check to skip the state update.
export function pruneFiltersToColumns(filters, columnNames) {
  const kept = Object.keys(filters).filter((name) => columnNames.has(name));
  if (kept.length === Object.keys(filters).length) return filters;
  return Object.fromEntries(kept.map((name) => [name, filters[name]]));
}

export function buildPreAggFilterSpecs(
  outputColumns,
  valueFilters,
  dateFilters,
  numberFilters,
  getColType
) {
  const outputColNames = new Set(outputColumns.map((c) => c.column));
  const filters = [];
  for (const [col, values] of Object.entries(valueFilters)) {
    if (!outputColNames.has(col) || values.size === 0) continue;
    filters.push(
      buildValueFilterSpec(col, values, getColType(col) === BOOL_COLUMN_TYPE)
    );
  }
  for (const [col, range] of Object.entries(dateFilters)) {
    if (!outputColNames.has(col)) continue;
    filters.push(...buildDateFilterSpecs(col, range));
  }
  for (const [col, range] of Object.entries(numberFilters)) {
    if (!outputColNames.has(col)) continue;
    filters.push(...buildNumberFilterSpecs(col, range));
  }
  return filters;
}

// The specification whose summary populates the builder's filter panels. It is
// the report's own specification - same sources, same joins - so every selected
// column is covered, including ones sourced from another report. Grouping and
// the pre-aggregation filters are dropped: the panels filter the
// pre-aggregation rows, and a panel must offer a column's whole domain rather
// than only the values its own filter has already let through. Returns null
// when there is nothing to summarise or the specification is not yet valid (a
// second source table with no join, say), so a half-built report is never sent.
export function buildPreAggSummarySpec(
  sourceTables,
  joinConfigs,
  outputColumns
) {
  if (outputColumns.length === 0) return null;
  const builder = new SpecBuilder(null);
  builder.setSourceTables(sourceTables);
  builder.setJoins(joinConfigs);
  builder.setGrouping(null);
  builder.setTypedColumns(outputColumns);
  builder.setPreAggregationFilters(null);
  if (builder.clean()) return null;
  return builder.get();
}

// Qualifies a display name shared by several output columns with the display
// name of the source each came from, so "Name" from two sources reads
// "Name - Brokers" and "Name - Underwriters" in the report.
export function qualifySharedDisplayNames(outputColumns, sourceDisplayNameMap) {
  // Counting is done on the unqualified name so that a name qualified by an
  // earlier build still counts against the name it was qualified from;
  // otherwise reopening a saved report leaves its twin unqualified. A
  // qualifier is only ever added, never taken away.
  const baseNames = outputColumns.map((col) => {
    const name = col.displayName ?? col.column;
    const source = sourceDisplayNameMap[col.sourceTable];
    const suffix = `${getColumnSourceSeparator()}${source}`;
    return source && name.endsWith(suffix)
      ? name.slice(0, -suffix.length)
      : name;
  });

  const counts = {};
  for (const name of baseNames) counts[name] = (counts[name] ?? 0) + 1;

  return outputColumns.map((col, index) => {
    const name = baseNames[index];
    const source = sourceDisplayNameMap[col.sourceTable];
    if (counts[name] < 2 || !source) return col;
    const displayName = `${name}${getColumnSourceSeparator()}${source}`;
    return displayName === col.displayName ? col : { ...col, displayName };
  });
}

// One single-source summary specification per selected source, used when the
// joined summary cannot be built because the sources do not yet fold into a
// single result. Each source is summarised on its own so the filter panels
// still offer that source's values while the joins are being set up.
export function buildPerSourceSummarySpecs(sourceTables, outputColumns) {
  if (outputColumns.length === 0) return [];
  return (sourceTables ?? [])
    .map((sourceTable) => {
      const columns = outputColumns.filter(
        (col) => col.sourceTable === sourceTable
      );
      if (columns.length === 0) return null;
      const builder = new SpecBuilder(null);
      builder.setSourceTables([sourceTable]);
      builder.setJoins(null);
      builder.setGrouping(null);
      builder.setTypedColumns(columns);
      builder.setPreAggregationFilters(null);
      if (builder.clean()) return null;
      return builder.get();
    })
    .filter(Boolean);
}

export function disambiguateColumns(columns) {
  const counts = {};
  for (const col of columns) {
    counts[col.column] = (counts[col.column] || 0) + 1;
  }
  const seen = new Set();
  return columns.map((col) => {
    const original = col.column;
    if (counts[col.column] > 1 && col.sourceTable) {
      const qualified = `${col.column}_${col.sourceTable}`;
      seen.add(qualified);
      return { ...col, column: qualified, sourceColumn: original };
    }
    if (seen.has(col.column)) {
      const qualified = `${col.column}_${col.sourceTable || ""}`;
      seen.add(qualified);
      return { ...col, column: qualified, sourceColumn: original };
    }
    seen.add(col.column);
    return { ...col, sourceColumn: original };
  });
}

export function pruneConfiguration(configuration, columnSet) {
  const result = {};
  for (const [internalName, tabConfig] of Object.entries(configuration)) {
    const pruned = {};
    if (tabConfig.widths) {
      const prunedWidths = {};
      for (const [col, width] of Object.entries(tabConfig.widths)) {
        if (columnSet.has(col)) prunedWidths[col] = width;
      }
      if (Object.keys(prunedWidths).length > 0) pruned.widths = prunedWidths;
    }
    if (tabConfig.metrics) {
      const prunedMetrics = tabConfig.metrics.filter((m) =>
        columnSet.has(m.key)
      );
      if (prunedMetrics.length > 0) pruned.metrics = prunedMetrics;
    }
    if (Object.keys(pruned).length > 0) result[internalName] = pruned;
  }
  return result;
}

export function isSpecValid(
  reportName,
  selectedTables,
  selectedColumns,
  joinConfigs
) {
  if (!reportName.trim()) return false;
  if (selectedTables.length === 0) return false;
  if (selectedColumns.length === 0) return false;
  if (selectedTables.length > 1) {
    if (!joinsFormSingleResult(selectedTables, joinConfigs)) return false;
  }
  return true;
}

// The one thing to tell the user about why Save and Create are disabled, or
// null when neither is blocked by the specification.
export function saveDisabledReason({
  selectedSources,
  selectedColumns,
  joinConfigs,
  reportName,
  isNameKnown,
}) {
  if ((selectedColumns ?? []).length === 0) return SAVE_BLOCKED_NO_COLUMNS;
  if (
    (selectedSources ?? []).length > 1 &&
    !joinsFormSingleResult(selectedSources, joinConfigs)
  ) {
    return SAVE_BLOCKED_JOINS_INCOMPLETE;
  }
  if (!(reportName ?? "").trim()) return SAVE_BLOCKED_NAME_MISSING;
  if (isNameKnown) return SAVE_BLOCKED_NAME_NOT_UNIQUE;
  return null;
}

// Derive the source reports the editor should pre-select when an existing
// MULTI_TABLE_REPORT is opened. The correct set is the REPORT:-prefixed entries
// of the primary output report's (the child with falsy notForOutput)
// sourceTables, with the prefix stripped - i.e. the reports that the output
// report depends on directly. Transitive dependencies (reports referenced only
// by other source reports) are deliberately excluded; they are re-included
// automatically at rebuild time via the save path.
export function deriveSelectedReports(existingReport) {
  if (!existingReport || existingReport.type !== "MULTI_TABLE_REPORT") {
    return [];
  }
  const subReports = existingReport.reports ?? [];
  const spec = subReports.find((r) => !r.notForOutput)?.specification ?? null;
  return (spec?.sourceTables ?? [])
    .filter((t) => t.startsWith("REPORT:"))
    .map((t) => t.replace(/^REPORT:/, ""));
}

export function isReportNameKnown(reportName, savedReports) {
  const trimmed = reportName.trim().toLowerCase();
  if (!trimmed) return false;
  const knownNames = new Set(
    (savedReports ?? []).map((r) => r.displayName?.toLowerCase())
  );
  return knownNames.has(trimmed);
}

// Reconcile the current column selection against the set of column names that
// are actually available from the selected sources. A source that is itself a
// report can stop returning a column, in which case any selection that depends
// on it must be cleaned up:
//   - Regular columns whose column is no longer available are dropped.
//   - Function columns (dataType "aggregate") are dropped when any column they
//     reference is gone. Their own name counts as referenceable so that other
//     selections may legitimately depend on them, so the filter is iterated to
//     a fixed point: dropping one function column drops the function columns
//     that referenced it, and so on down the chain.
//   - Group-aggregate columns (dataType "groupAggregate") are dropped when
//     their source column is gone, or when grouping is switched off.
//   - Grouping is switched off entirely if any group-by column has disappeared.
//   - Each group rollup entry is kept only while its output column survives -
//     this covers both source columns and function columns, rather than
//     requiring the entry's column to itself be an available source column.
// The original outputColumns / groupSpec references are returned unchanged when
// nothing was removed, so callers can use identity checks to skip updates.
export function reconcileSelectionWithAvailableColumns({
  outputColumns,
  groupSpec,
  availableColumnNames,
}) {
  const isAvailable = (name) => availableColumnNames.has(name);

  const groupingDisabled =
    !!groupSpec?.[0] &&
    groupSpec[0].groupColumns.some((gc) => !isAvailable(gc));

  const isKept = (col, referenceableNames) => {
    if (col.dataType === "aggregate") {
      const required =
        col.requiredColumns ?? extractVarColumns(col.expression ?? {});
      return required.every((name) => referenceableNames.has(name));
    }
    if (col.dataType === "groupAggregate") {
      if (groupingDisabled) return false;
      return isAvailable(col.sourceColumn);
    }
    return isAvailable(col.column);
  };

  let nextOutputColumns = outputColumns;
  let removedSomething = true;
  while (removedSomething) {
    const referenceableNames = new Set(availableColumnNames);
    nextOutputColumns.forEach((col) => {
      if (col.dataType === "aggregate") referenceableNames.add(col.column);
    });
    const survivors = nextOutputColumns.filter((col) =>
      isKept(col, referenceableNames)
    );
    removedSomething = survivors.length !== nextOutputColumns.length;
    nextOutputColumns = survivors;
  }

  const keptColumnNames = new Set(nextOutputColumns.map((c) => c.column));
  let nextGroupSpec = groupSpec;
  if (groupingDisabled) {
    nextGroupSpec = null;
  } else if (groupSpec?.[0]) {
    const filteredAggregate = groupSpec[0].aggregate.filter((a) =>
      keptColumnNames.has(a.column)
    );
    if (filteredAggregate.length !== groupSpec[0].aggregate.length) {
      nextGroupSpec = [{ ...groupSpec[0], aggregate: filteredAggregate }];
    }
  }

  return {
    outputColumns:
      nextOutputColumns.length === outputColumns.length
        ? outputColumns
        : nextOutputColumns,
    groupSpec: nextGroupSpec,
  };
}

// Compute the next outputColumns / groupSpec when the report's column selection
// changes while grouping is active. The behaviour:
//   - Adding a function column (dataType "aggregate") switches grouping off
//     entirely rather than giving it a default group rollup.
//   - Columns removed from the selection drop their group rollup entry and, if
//     they were group-by columns, are removed from groupColumns. If that leaves
//     no group-by columns, grouping is switched off.
//   - Columns added to the selection get a default group rollup derived from
//     their data type (via getDefaultAggregation).
//   - When grouping is switched off, groupAggregate output columns are removed.
// The original groupSpec reference is returned when grouping is unaffected, so
// callers can use an identity check to skip the state update.
export function applyOutputColumnChange({
  outputColumns,
  groupSpec,
  newColumns,
  availableColumns,
}) {
  if (!groupSpec?.[0]) {
    return { outputColumns: newColumns, groupSpec };
  }

  const currentColumnKeys = new Set(outputColumns.map((c) => c.column));
  const addedFunctionColumn = newColumns.some(
    (c) => c.dataType === "aggregate" && !currentColumnKeys.has(c.column)
  );
  if (addedFunctionColumn) {
    return {
      outputColumns: newColumns.filter((c) => c.dataType !== "groupAggregate"),
      groupSpec: null,
    };
  }

  const currentNonAggKeys = new Set(
    outputColumns
      .filter((c) => c.dataType !== "groupAggregate")
      .map((c) => c.column)
  );
  const newNonAggKeys = new Set(
    newColumns
      .filter((c) => c.dataType !== "groupAggregate")
      .map((c) => c.column)
  );
  const removedColumns = new Set(
    [...currentNonAggKeys].filter((col) => !newNonAggKeys.has(col))
  );
  const existingAggKeys = new Set(groupSpec[0].aggregate.map((a) => a.column));
  const addedColumns = newColumns.filter(
    (c) =>
      c.dataType !== "groupAggregate" &&
      !currentNonAggKeys.has(c.column) &&
      !existingAggKeys.has(c.column)
  );

  let nextGroupSpec = groupSpec;
  if (removedColumns.size > 0 || addedColumns.length > 0) {
    let newAggregate = groupSpec[0].aggregate;
    let newGroupColumns = groupSpec[0].groupColumns;

    if (removedColumns.size > 0) {
      newAggregate = newAggregate.filter(
        (a) => !removedColumns.has(a.expression?.sourceColumn ?? a.column)
      );
      newGroupColumns = newGroupColumns.filter((gc) => !removedColumns.has(gc));
    }

    if (newGroupColumns.length === 0) {
      nextGroupSpec = null;
    } else {
      if (addedColumns.length > 0) {
        const newAggKeys = new Set(newAggregate.map((a) => a.column));
        const addedEntries = addedColumns
          .filter((c) => !newAggKeys.has(c.column))
          .map((c) => {
            const def = availableColumns?.find(
              (ac) => ac.internalName === c.column
            );
            const func = getDefaultAggregation(def?.type, def?.subType);
            return {
              column: c.column,
              expression: { func, sourceColumn: c.column },
            };
          });
        if (addedEntries.length > 0) {
          newAggregate = [...newAggregate, ...addedEntries];
        }
      }
      nextGroupSpec = [
        {
          ...groupSpec[0],
          groupColumns: newGroupColumns,
          aggregate: newAggregate,
        },
      ];
    }
  }

  if (nextGroupSpec === null) {
    return {
      outputColumns: newColumns.filter((c) => c.dataType !== "groupAggregate"),
      groupSpec: null,
    };
  }

  return { outputColumns: newColumns, groupSpec: nextGroupSpec };
}
